<script setup lang="ts" generic="TValue, TKey extends string | number">
	import {
		computed,
		nextTick,
		onUnmounted,
		ref,
		watch,
		type VNodeRef,
	} from "vue";

	const props = defineProps<{
		modelValue: TKey[];
		options: TValue[];
		placeholder?: string;
		itemKey: (item: TValue) => TKey;
		alwaysOpen?: boolean;
	}>();

	const emit = defineEmits(["update:modelValue"]);

	const open = ref(false);
	const trigger = ref<HTMLElement | null>(null);
	const optionRefs = ref<HTMLElement[]>([]);
	const focusedIndex = ref(-1);

	const triggerId = `multiselect-trigger-${__MULTISELECT_UID__}`;
	const listboxId = `multiselect-listbox-${__MULTISELECT_UID__}`;

	const selectedValues = computed(() => props.modelValue || []);
	const selectedItems = computed(() =>
		props.options.filter(o =>
			selectedValues.value.includes(props.itemKey(o)),
		),
	);

	function isSelected(item: TValue) {
		return selectedValues.value.includes(props.itemKey(item));
	}

	function toggle() {
		open.value = !open.value;
		if (open.value) nextTick(() => focusOption(0));
	}

	function openAndFocus(idx: number) {
		open.value = true;
		nextTick(() => focusOption(idx));
	}

	function focusOption(idx: number) {
		if (props.options.length === 0) return;
		focusedIndex.value = Math.max(
			0,
			Math.min(idx, props.options.length - 1),
		);
		const el = optionRefs.value[focusedIndex.value];
		el?.focus?.();
	}

	function setOptionRef(el: Element | null) {
		if (!el) return;
		const idx = Array.from(el.parentElement?.children || []).indexOf(el);
		optionRefs.value[idx] = el as HTMLElement;
	}

	function optionId(idx: number) {
		return `multiselect-option-${__MULTISELECT_UID__}-${idx}`;
	}

	function toggleSelect(item: TKey) {
		const newVals = [...selectedValues.value];
		const idx = newVals.indexOf(item);
		if (idx === -1) newVals.push(item);
		else newVals.splice(idx, 1);
		emit("update:modelValue", newVals);
	}

	function onListboxKeydown(e: KeyboardEvent) {
		const key = e.key;
		if (key === "ArrowDown")
			focusOption((focusedIndex.value + 1) % props.options.length);
		else if (key === "ArrowUp")
			focusOption(
				(focusedIndex.value - 1 + props.options.length) %
					props.options.length,
			);
		else if (key === "Home") focusOption(0);
		else if (key === "End") focusOption(props.options.length - 1);
		else if (key === " " || key === "Enter") {
			const item = props.options[focusedIndex.value];
			if (item) toggleSelect(props.itemKey(item));
		} else if (key === "Escape" && !props.alwaysOpen)
			closeAndFocusTrigger();
	}

	function closeAndFocusTrigger() {
		open.value = false;
		nextTick(() => trigger.value?.focus());
	}

	function onClickOutside(e: MouseEvent) {
		if (props.alwaysOpen) return;
		const root = trigger.value?.closest(
			`#multi-select-root-${__MULTISELECT_UID__}`,
		);
		if (!root) return;
		if (!root.contains(e.target as Node)) open.value = false;
	}

	if (typeof window !== "undefined") {
		window.addEventListener("click", onClickOutside);
	}

	onUnmounted(() => {
		if (typeof window !== "undefined")
			window.removeEventListener("click", onClickOutside);
	});

	watch(
		() => props.options.length,
		n => {
			if (n === 0) focusedIndex.value = -1;
			else focusedIndex.value = Math.min(focusedIndex.value, n - 1);
		},
	);
</script>

<script lang="ts">
	let __MULTISELECT_UID__ = Math.random().toString(36).slice(2, 9);
</script>

<template>
	<div
		:class="alwaysOpen ? 'w-64' : 'relative h-12 w-64'"
		:id="'multi-select-root-' + __MULTISELECT_UID__"
	>
		<div
			:class="
				alwaysOpen
					? 'flex w-full flex-col overflow-hidden rounded-sm border border-gray-300 bg-green-50'
					: 'absolute top-0 left-0 z-10 flex w-full flex-col overflow-hidden rounded-sm border border-gray-300 bg-green-50'
			"
		>
			<button
				v-if="!alwaysOpen"
				ref="trigger"
				:id="triggerId"
				type="button"
				class="flex h-12 w-full shrink-0 items-center justify-between rounded-t px-3 py-2 focus:ring focus:outline-none"
				@click="toggle()"
				@keydown.down.prevent="openAndFocus(0)"
				@keydown.enter.prevent="toggle()"
				@keydown.space.prevent="toggle()"
				aria-haspopup="listbox"
				:aria-expanded="open"
				:aria-controls="listboxId"
			>
				<div class="truncate">
					<slot
						name="label"
						:selected="selectedItems"
					/>
				</div>
			</button>

			<Transition name="dropdown">
				<div
					v-show="alwaysOpen || open"
					class="flex flex-col gap-2 overflow-hidden px-1 pt-2"
				>
					<slot name="before-listbox" />

					<ul
						ref="listbox"
						:id="listboxId"
						class="flex max-h-60 flex-col overflow-auto py-1"
						role="listbox"
						:aria-multiselectable="true"
						:aria-labelledby="alwaysOpen ? undefined : triggerId"
						@keydown.stop.prevent="onListboxKeydown"
					>
						<li
							v-for="(item, idx) in options"
							:key="itemKey(item)"
							:id="optionId(idx)"
							:ref="setOptionRef as VNodeRef"
							role="option"
							:aria-selected="isSelected(item)"
							tabindex="-1"
							class="flex cursor-pointer items-center rounded-sm p-2 hover:bg-gray-100 focus:bg-gray-100"
							@click.stop="toggleSelect(itemKey(item))"
							@mouseenter="focusedIndex = idx"
						>
							<slot
								name="option"
								:item="item"
								:selected="isSelected(item)"
							/>
						</li>
					</ul>
				</div>
			</Transition>
		</div>
	</div>
</template>

<style scoped>
	.dropdown-enter-from,
	.dropdown-leave-to {
		max-height: 0;
		opacity: 0;
	}

	.dropdown-enter-to,
	.dropdown-leave-from {
		max-height: 500px;
		opacity: 1;
	}

	.dropdown-enter-active,
	.dropdown-leave-active {
		transition:
			max-height 0.3s ease,
			opacity 0.3s ease;
	}
</style>
